import { useState, useEffect } from "react";
import { 
  ShieldCheckIcon, 
  BeakerIcon, 
  TrophyIcon, 
  ClockIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const SafetyStandards = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const standards = [
    { icon: ShieldCheckIcon, title: "GMP Certified", desc: "Manufactured under strict quality guidelines", color: "from-blue-500 to-cyan-500" },
    { icon: BeakerIcon, title: "Lab Tested", desc: "Third-party tested for purity and potency", color: "from-purple-500 to-pink-500" },
    { icon: TrophyIcon, title: "Premium Quality", desc: "Highest grade pharmaceutical ingredients", color: "from-amber-500 to-orange-500" },
    { icon: ClockIcon, title: "Fresh Stock", desc: "Regular manufacturing with expiry tracking", color: "from-green-500 to-emerald-500" },
  ];

  const certifications = [
    "ISO 9001:2024 Certified",
    "GMP Compliant Facility",
    "FDA Registered",
    "EU Quality Standards"
  ];

  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      {/* Animated Background Elements - Light version */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-100 to-amber-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-60"></div>
        
        
        {/* Grid Pattern - Safe Version */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-12 lg:mb-16 transform transition-all duration-700 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <span className="inline-block px-4 py-2 bg-gradient-to-r from-red-50 to-amber-50 text-red-600 rounded-full text-sm font-semibold tracking-wider border border-red-100">
            ✦ QUALITY ASSURANCE ✦
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-6 leading-tight">
            <span className="text-gray-900">Our Commitment to</span>
            <br />
            <span className="bg-gradient-to-r from-red-600 to-amber-600 bg-clip-text text-transparent">
              Safety & Excellence
            </span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4 text-lg">
            Setting the highest standards in pharmaceutical-grade supplements
          </p>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Image Section */}
          <div className={`relative transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            {/* Image Container with Effects */}
            <div className="relative group">
              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-amber-600 rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
              
              {/* Image Frame */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://darkdraganabolic.in/wp-content/uploads/2023/04/safety-standard.jpg"
                  alt="Safety Standards - Premium Quality Control"
                  className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {/* Badge */}
                <div className="absolute bottom-6 left-6 flex items-center gap-3 bg-white/95 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-full shadow-lg border border-gray-200">
                  <CheckBadgeIcon className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-semibold">ISO 9001:2024 Certified</span>
                </div>
              </div>
            </div>

            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl border border-gray-100 hidden lg:block">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">15+</span>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Years of</p>
                  <p className="text-sm font-bold text-gray-900">Quality Excellence</p>
                </div>
              </div>
            </div>

            {/* Certification Badges */}
            <div className="absolute top-6 -left-6 hidden lg:block">
              <div className="bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                <div className="space-y-2">
                  {certifications.slice(0, 2).map((cert, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700 font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className={`space-y-8 transform transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            
            {/* Description */}
            <div className="prose prose-lg">
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                The bodybuilding market has become flooded with a large number of unscrupulous 
                companies selling low-grade steroids. The products these companies promote are 
                not standardized and therefore pose serious health risks. 
              </p>
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed mt-4">
                At <span className="font-bold text-gray-900">Dark Drag Anabolic</span>, we place a 
                premium on offering absolutely effective and safe steroids which gives our customers 
                peace of mind and complete satisfaction.
              </p>
            </div>

            {/* Standards Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              {standards.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={index}
                    className={`group p-5 bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
                  >
                    <div className={`w-10 h-10 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Certifications List */}
            {/*<div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ShieldCheckIcon className="w-4 h-4 text-red-600" />
                Our Certifications
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>{cert}</span>
                  </div>
                ))}
              </div>
            </div> */}

            {/* CTA Buttons */}
            {/*<div className="flex flex-wrap gap-4 pt-4">
              <button className="group px-6 py-3 bg-gradient-to-r from-red-600 to-amber-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-red-600/20 transition-all duration-300 flex items-center gap-2">
                <DocumentTextIcon className="w-5 h-5" />
                View Certifications
                <ArrowTopRightOnSquareIcon className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
              <button className="px-6 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:border-red-600 hover:text-red-600 transition-all duration-300 flex items-center gap-2">
                Download Brochure
              </button>
            </div> */}

            {/* Trust Indicators */}
            <div className="flex items-center gap-6 pt-6 border-t border-gray-200">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-xs text-gray-600 font-bold">✓</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  Trusted by <span className="font-bold text-gray-900">10,000+</span> customers worldwide
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className="text-amber-400 text-xs">★</span>
                  ))}
                  <span className="text-xs text-gray-500 ml-1">4.9/5 Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Features */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 transform transition-all duration-1000 delay-500 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          {[
            { label: "Products Tested", value: "5000+" },
            { label: "Happy Customers", value: "10k+" },
            { label: "Years Experience", value: "15+" },
            { label: "Quality Checks", value: "100+" },
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-4 text-center border border-gray-200 shadow-md">
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SafetyStandards;